import { useEffect, useMemo, useState } from "react";
import type { FiatPrice } from "@b2bportal/air-shopping-api";
import type { Product } from "@b2bportal/purchase-api";
import { useCheckoutSend, useCheckoutStateSelector } from "@checkout/context";
import { useI18nContext } from "@hopper-b2b/i18n";
import { productTotalSelectorByProduct } from "@hsbc/utilities";
import {
  getWalletCreditPayAmount,
  type WalletPaymentEvent,
  WalletPaymentEventTypes,
} from "@hsbc/wallet";
import { useCreditsBalance } from "../../CreditHistory";

interface UseTravelCreditSelectionReturn {
  context: {
    creditSelected: boolean;
    isCreditSelectionDisabled: boolean;
    travelCreditBalance: string;
    travelCreditPayAmount: string;
  };
  handlers: {
    onCheckboxClick: () => void;
  };
}

export const useTravelCreditSelection = (
  product?: Product
): UseTravelCreditSelectionReturn => {
  const { formatCurrency } = useI18nContext();
  const [creditSelected, setCreditSelected] = useState<boolean>(true);

  const send = useCheckoutSend<WalletPaymentEvent>();
  const { fiat: totalPrice } = useCheckoutStateSelector(
    productTotalSelectorByProduct(product)
  );
  const creditPayAmount: FiatPrice = useCheckoutStateSelector(
    getWalletCreditPayAmount
  );

  const { creditBalance, totalCredits: travelCreditBalance } =
    useCreditsBalance(true);

  const disableForNoCredit = useMemo(() => {
    return !creditBalance?.value || creditBalance?.value <= 0;
  }, [creditBalance]);

  // Depend on the primitive total value rather than the totalPrice object: the
  // selector can return a new object on every render, which would cause
  // "Maximum update depth exceeded". The credit is clamped to the total and
  // recomputed whenever the total changes (e.g. once the quote arrives), so it
  // never exceeds the amount to pay.
  const totalPriceValue = Math.max(totalPrice?.value ?? 0, 0);
  const creditAmountToUse: FiatPrice = useMemo(
    () => ({
      ...creditBalance,
      value: creditSelected
        ? Math.min(totalPriceValue, creditBalance?.value ?? 0)
        : 0,
    }),
    [creditBalance, creditSelected, totalPriceValue]
  );

  useEffect(() => {
    setCreditSelected(!disableForNoCredit);
  }, [disableForNoCredit]);

  useEffect(() => {
    if (disableForNoCredit) return;

    send({
      type: WalletPaymentEventTypes.SET_WALLET_CREDIT_PAY_AMOUNT,
      amount: creditAmountToUse,
    });

    send({
      type: WalletPaymentEventTypes.SET_WALLET_CREDIT_BALANCE,
      amount: creditBalance,
    });
  }, [disableForNoCredit, send, creditAmountToUse, creditBalance]);

  return {
    context: {
      creditSelected,
      isCreditSelectionDisabled: disableForNoCredit,
      travelCreditBalance,
      travelCreditPayAmount: formatCurrency(
        creditPayAmount ? creditPayAmount?.value : 0
      ),
    },
    handlers: {
      onCheckboxClick: () => {
        setCreditSelected(!creditSelected);
      },
    },
  };
};
